import React, { useState, useEffect } from 'react';
import { MediaAsset } from '../../../models/media';
import { MediaManager } from '../MediaManager/MediaManager';
import { useApi } from '../../../hooks/useApi';
import styles from './VideoSelector.module.css';

interface VideoData {
  provider?: string;
  video_id?: string;
  video_url?: string;
  duration_sec?: number;
}

interface VideoSelectorProps {
  value?: VideoData | null;
  onChange: (videoData: VideoData | null) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  label?: string;
  error?: string;
  preloadedVideo?: MediaAsset | null; // Добавляем возможность передать готовый объект
}

export const VideoSelector: React.FC<VideoSelectorProps> = ({
  value,
  onChange,
  placeholder = 'Выберите видео',
  disabled = false,
  required = false,
  label,
  error,
  preloadedVideo,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<MediaAsset | null>(null);
  const api = useApi();

  // Загружаем данные видео при изменении value или preloadedVideo
  useEffect(() => {
    if (preloadedVideo && value?.video_id === preloadedVideo.id) {
      setSelectedVideo(preloadedVideo);
    } else if (value?.video_id && !selectedVideo) {
      // В реальном приложении здесь будет запрос к API для получения видео по ID
      // api.media.getMediaById(value.video_id).then(setSelectedVideo);
    } else if (!value) {
      setSelectedVideo(null);
    }
  }, [value, selectedVideo, api, preloadedVideo]);

  const handleSelect = (media: MediaAsset | null) => {
    if (media && media.type === 'video') {
      // Извлекаем данные видео из MediaAsset
      const videoData: VideoData = {
        provider: getVideoProvider(media),
        video_id: media.id,
        video_url: getVideoUrl(media),
        duration_sec: getVideoDuration(),
      };
      onChange(videoData);
      setSelectedVideo(media);
    } else {
      onChange(null);
      setSelectedVideo(null);
    }
    setIsModalOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setSelectedVideo(null);
  };

  const getVideoProvider = (media: MediaAsset): string => {
    // Извлекаем провайдера из структуры MediaAsset
    if (
      media.type === 'video' &&
      media.variants &&
      'meta' in media.variants &&
      'provider' in media.variants.meta
    ) {
      return media.variants.meta.provider;
    }
    return 'self';
  };

  const getVideoUrl = (media: MediaAsset): string => {
    // Получаем URL видео из структуры MediaAsset
    if (media.variants && 'embed' in media.variants) {
      return media.variants.embed.url;
    }
    return media.original_url || '';
  };

  const getVideoDuration = (): number | undefined => {
    // В структуре MediaAsset может быть duration, если нет - возвращаем undefined
    return undefined;
  };

  const getThumbnailUrl = (media: MediaAsset): string => {
    // Получаем thumbnail для видео
    if (
      media.variants &&
      'thumbnail' in media.variants &&
      media.variants.thumbnail
    ) {
      return media.variants.thumbnail.url;
    }

    // Fallback: пробуем извлечь video_id и сгенерировать YouTube thumbnail
    if (media.type === 'video' && media.provider === 'youtube') {
      const videoId = extractVideoIdFromMedia(media);
      if (videoId) {
        return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
      }
    }

    return '';
  };

  // Функция для извлечения video_id из MediaAsset
  const extractVideoIdFromMedia = (media: MediaAsset): string | null => {
    // Сначала проверяем meta.video_id
    if (
      media.variants &&
      'meta' in media.variants &&
      'video_id' in media.variants.meta
    ) {
      return media.variants.meta.video_id as string;
    }

    // Затем проверяем ID самого медиа
    if (media.id && /^[a-zA-Z0-9_-]{11}$/.test(media.id)) {
      return media.id;
    }

    // Пробуем извлечь из URL в зависимости от провайдера
    if (media.original_url) {
      if (media.provider === 'youtube') {
        return extractYouTubeVideoIdFromUrl(media.original_url);
      } else if (media.provider === 'accelsite') {
        return extractAccelsiteVideoIdFromUrl(media.original_url);
      }
      // Fallback для YouTube если провайдер не указан
      return extractYouTubeVideoIdFromUrl(media.original_url);
    }

    return null;
  };

  // Функция для извлечения YouTube video ID из URL
  const extractYouTubeVideoIdFromUrl = (url: string): string | null => {
    if (!url) {
      return null;
    }

    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
      /youtu\.be\/([^&\n?#]+)/,
      /youtube\.com\/embed\/([^&\n?#]+)/,
      /youtube\.com\/v\/([^&\n?#]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    return null;
  };

  // Функция для извлечения Accelsite video ID из URL
  const extractAccelsiteVideoIdFromUrl = (url: string): string | null => {
    if (!url) {
      return null;
    }

    // Паттерн для Accelsite URL: https://v.accelsite.io/v/VIDEO_ID
    const accelsitePattern = /(?:v\.accelsite\.io\/v\/)([^&\n?#]+)/;
    const match = url.match(accelsitePattern);

    if (match && match[1]) {
      return match[1];
    }

    return null;
  };

  // Компонент для отображения thumbnail с fallback
  const VideoThumbnail: React.FC<{ media: MediaAsset }> = ({ media }) => {
    const [thumbnailError, setThumbnailError] = useState(false);
    const [currentThumbnailIndex, setCurrentThumbnailIndex] = useState(0);

    const videoId = extractVideoIdFromMedia(media);
    const provider = media.provider;

    // Для Accelsite показываем кастомный плейсхолдер
    if (provider === 'accelsite') {
      return (
        <div className={styles.videoPlaceholder}>
          <span>🎬</span>
          <span>Видео из AXL</span>
        </div>
      );
    }

    // Различные варианты YouTube thumbnail в порядке качества
    const thumbnailVariants =
      videoId && provider === 'youtube'
        ? [
            `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
            `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
            `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
            `https://img.youtube.com/vi/${videoId}/default.jpg`,
          ]
        : [];

    const handleImageError = () => {
      if (currentThumbnailIndex < thumbnailVariants.length - 1) {
        setCurrentThumbnailIndex((prev) => prev + 1);
      } else {
        setThumbnailError(true);
      }
    };

    const currentThumbnail =
      getThumbnailUrl(media) || thumbnailVariants[currentThumbnailIndex];

    if (thumbnailError || !currentThumbnail) {
      return (
        <div className={styles.videoPlaceholder}>
          <span>🎥</span>
          <span>Видео</span>
        </div>
      );
    }

    return (
      <img
        src={currentThumbnail}
        alt="Превью видео"
        className={styles.previewImage}
        onError={handleImageError}
      />
    );
  };

  const formatDuration = (seconds?: number): string => {
    if (!seconds) {
      return '';
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getProviderLabel = (provider: string): string => {
    switch (provider) {
      case 'youtube':
        return 'YouTube';
      case 'accelsite':
        return 'Accelsite LMS';
      case 'vimeo':
        return 'Vimeo';
      case 'self':
        return 'Собственное видео';
      default:
        return provider;
    }
  };

  return (
    <div className={styles.container}>
      {label && (
        <label className={styles.label}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div
        className={`${styles.selector} ${error ? styles.error : ''} ${
          disabled ? styles.disabled : ''
        }`}
      >
        {value && selectedVideo ? (
          <div className={styles.selectedVideo}>
            <div className={styles.preview}>
              <VideoThumbnail media={selectedVideo} />
            </div>
            <div className={styles.videoInfo}>
              <div className={styles.videoProvider}>
                {getProviderLabel(value.provider || 'self')}
              </div>
              {value.duration_sec && (
                <div className={styles.videoDuration}>
                  {formatDuration(value.duration_sec)}
                </div>
              )}
            </div>
            <div className={styles.actions}>
              <button
                type="button"
                onClick={() => setIsModalOpen(true)}
                className={styles.changeButton}
                disabled={disabled}
              >
                Изменить
              </button>
              <button
                type="button"
                onClick={handleClear}
                className={styles.clearButton}
                disabled={disabled}
              >
                ✕
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className={styles.selectButton}
            disabled={disabled}
          >
            <span className={styles.selectIcon}>🎥</span>
            <span className={styles.selectText}>{placeholder}</span>
          </button>
        )}
      </div>

      {error && <div className={styles.errorText}>{error}</div>}

      {isModalOpen && (
        <MediaManager
          isOpen={true}
          onSelect={handleSelect}
          onClose={() => setIsModalOpen(false)}
          title="Выберите видео"
          mediaType="video"
        />
      )}
    </div>
  );
};
